/**
 * AURA Orientation Effects Orchestrator
 * Coordinates all orientation sub-effects and provides a unified API.
 * Individual effect implementations live in dedicated sub-modules
 * (reading-aids, media-motion, focus-cursor) for maintainability.
 */

import { OrientationSettings } from '../../core/types';
import { Logger } from '../../core/logger';
import {
  applyReadMode,
  applyReadingGuide,
  applyReadingMask,
  destroyReadingAids,
} from './reading-aids';
import {
  applyStopAnimations,
  applyHideImages,
  applyMuteSounds,
  destroyMediaMotion,
} from './media-motion';
import {
  applyHighlightHover,
  applyHighlightFocus,
  applyCursors,
} from './focus-cursor';

export {
  muteAllMedia,
  unmuteAllMedia,
  pauseAllGifs,
  resumeAllGifs,
} from './media-motion';

const logger = new Logger('OrientationEffects');

/**
 * Apply all orientation styles based on current settings.
 * Handles CSS injection, DOM overlays, and imperative media controls
 * so that profile activation works the same as manual toggles.
 */
export function applyOrientationStyles(settings: OrientationSettings): void {
  logger.debug('Applying orientation styles');
  applyStopAnimations(settings.stopAnimations);
  applyHideImages(settings.hideImages);
  applyHighlightHover(settings.highlightHover);
  applyHighlightFocus(settings.highlightFocus);
  applyCursors(settings);
  applyMuteSounds(settings.muteSounds);
  applyReadMode(settings.readMode);
  applyReadingMask(settings.readingMask);
  applyReadingGuide(settings.readingGuide);
}

/**
 * Tear down all orientation effects, removing DOM elements
 * and event listeners that live outside the shadow-DOM shell.
 */
export function destroyOrientationEffects(): void {
  destroyReadingAids();
  destroyMediaMotion();
  logger.info('Orientation effects destroyed');
}
